import { WarehousingChart } from './warehousing-chart.model';
import { DashboardSummary } from './warehouse-reporting.model';

/**
 * Shapes behind the warehousing dashboard and the reconciliation report.
 *
 * The dashboard figures are currently derived in WarehousingDashboardService from the
 * read-only CRUD routes rather than a single aggregate endpoint. These classes are the
 * boundary that lets that swap for one server call without the screen changing.
 */

export class WarehousingKpis {
  /** SUM(qty) across every stock level. */
  totalStockQty: number = 0;

  /** Products holding a non-zero level. Zero is tested with the ledger epsilon, never `=== 0`. */
  productsHoldingStock: number = 0;

  /** Locations holding a non-zero level. */
  locationsInUse: number = 0;

  /** Movements written since midnight local time. */
  movementsToday: number = 0;
}

export class WarehouseOccupancy {
  warehouseId: number = -1;
  warehouseName: string = '';

  /** Locations defined at the site, whether or not they hold anything. */
  locationCount: number = 0;

  /** Locations actually holding stock. */
  locationsInUse: number = 0;

  totalQty: number = 0;
  productCount: number = 0;

  /** locationsInUse as a percentage of locationCount, for the progress bar. Capped at 100. */
  usedPercent: number = 0;

  /**
   * Locations holding stock that no longer exist in warehouse_location. Non-zero means an
   * integrity problem rather than a display one: with no foreign keys in this schema, a
   * stock level can outlive the location it points at.
   */
  orphanedLocations: number = 0;
}

/**
 * A stock level that needs a human to look at it. Negative means the ledger disagrees
 * with itself and stock has been lost, so it is never collapsed into the zero list.
 */
export class StockException {
  productStockLevelId: number = -1;

  productId: number = -1;
  productName: string = '';
  sku: string = '';

  warehouseLocationId: number = -1;
  warehouseId: number = -1;
  warehouseName: string = '';
  aisle: string = '';
  shelf: string = '';

  qty: number = 0;
}

export class RecentMovement {
  id: number = -1;
  createdAt: string = '';

  productId: number = -1;
  productName: string = '';
  sku: string = '';

  warehouseName: string = '';
  aisle: string = '';
  shelf: string = '';

  movementType: string = '';
  movementTypeLabel: string = '';

  qty: number = 0;

  /**
   * The movement's two scale readings, null where the goods were not weighed — the normal case.
   *
   * Carried so the feed can show what was weighed as it happened. Null and not 0, for the reason the
   * ledger row gives: zero is a real reading off a tared scale, so it cannot also mean "no reading".
   */
  startWeight: number = null;
  endWeight: number = null;

  referenceName: string = '';
  referenceCode: string = '';
}

export class WarehousingDashboard {
  kpis: WarehousingKpis = new WarehousingKpis();
  occupancy: WarehouseOccupancy[] = [];

  /** Kept apart from `zero` deliberately: one is a defect, the other is just an empty shelf. */
  negative: StockException[] = [];
  zero: StockException[] = [];

  recentMovements: RecentMovement[] = [];

  stockShareByWarehouse: WarehousingChart = new WarehousingChart();
  topProductsByQty: WarehousingChart = new WarehousingChart();
  movementsByType: WarehousingChart = new WarehousingChart();
  movementsByDay: WarehousingChart = new WarehousingChart();

  /** How many days of ledger the two movement charts cover, for the caption. */
  movementWindowDays: number = 0;

  /**
   * The server-computed summary, including valuation and its coverage.
   *
   * Carried alongside the locally derived figures rather than replacing them yet: the
   * server classifies zero and negative against a throughput-scaled tolerance this UI
   * cannot reproduce, so `summary.negativeStockCount` is the authoritative count even
   * where `negative.length` is what the callout lists.
   */
  summary: DashboardSummary = new DashboardSummary();

  /** False when the summary call failed, so no money figure is shown as fact. */
  summaryLoaded: boolean = false;
}

/**
 * One (product, location) pair with the stored level set against the level recomputed
 * from the ledger. A pair present on only one side is the interesting case, so both
 * quantities are nullable rather than defaulted to 0 — a missing level row and a level
 * row of genuinely zero are different problems.
 */
export class StockReconciliationRow {
  productId: number = -1;
  productName: string = '';
  sku: string = '';

  warehouseLocationId: number = -1;
  warehouseId: number = -1;
  warehouseName: string = '';
  aisle: string = '';
  shelf: string = '';

  /** null when no product_stock_level row exists for the pair. */
  levelQty: number = null;

  /** null when the ledger holds no movements for the pair. */
  ledgerQty: number = null;

  /** levelQty - ledgerQty, treating a missing side as 0. */
  variance: number = 0;

  movementCount: number = 0;
}
